import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import filter from 'lodash/filter';
import { useNetworkRequest } from './useNetworkRequest';

export type UseTermsOfServiceOptions = {
  accessId: string;
  signerId: string;
  groupKey: string;
  enabled?: boolean;
};

// PactSafe is a third party; without a deadline an outage there keeps this
// query loading forever, along with anything gated on it.
const PACTSAFE_TIMEOUT_MS = 10000;

export function useTermsOfService(options: UseTermsOfServiceOptions) {
  const { unsignedContractCount, isLoading, isSuccess, isError, refetch } =
    useUnsignedContractCount(options);

  const hasSignedLatestTerms = unsignedContractCount === 0;
  // True only when PactSafe successfully answered and reported unsigned
  // contracts — loading and error states are "unknown", not "unsigned".
  const hasConfirmedUnsignedTerms = isSuccess && (unsignedContractCount ?? 0) > 0;

  return {
    hasSignedLatestTerms,
    hasConfirmedUnsignedTerms,
    unsignedContractCount,
    isLoading,
    isSuccess,
    isError,
    refetch,
  };
}

export function useUnsignedContractCount(options: UseTermsOfServiceOptions) {
  const { accessId, groupKey, signerId, enabled } = options;
  const { axios } = useNetworkRequest();

  const checkContracts = async () => {
    const { data } = await axios.get(
      `https://pactsafe.io/latest?sid=${accessId}&gkey=${groupKey}&sig=${encodeURIComponent(
        signerId,
      )}`,
      { timeout: PACTSAFE_TIMEOUT_MS },
    );
    const numberOfUnsignedContracts = filter(data, (contract: unknown) => !contract).length;
    return numberOfUnsignedContracts;
  };

  const {
    data: unsignedContractCount,
    isLoading,
    isInitialLoading,
    isSuccess,
    isError,
    refetch,
  } = useQuery({
    enabled,
    queryKey: ['termsOfService', accessId, signerId, groupKey],
    queryFn: async () => {
      const numberOfUnsignedContracts = await checkContracts();
      return numberOfUnsignedContracts;
    },
    // The global QueryClient disables retries; a transient PactSafe blip
    // shouldn't immediately settle this query as errored.
    retry: 2,
    retryDelay: (attempt) => Math.min(2000 * 2 ** attempt, 15000),
  });

  return {
    unsignedContractCount,
    isLoading: enabled ? isLoading : isInitialLoading,
    isSuccess,
    isError,
    refetch,
  };
}

export type UseSignContractOptions = {
  accessId: string;
  signerId: string;
};

type AgreementParams = {
  groupId: string;
  versionIds: string[];
};

export function useSendContractAgreement(options: UseSignContractOptions) {
  const { accessId, signerId } = options;
  const { axios } = useNetworkRequest();
  const queryClient = useQueryClient();

  const sendActivity = async ({ groupId, versionIds }: AgreementParams) => {
    const sig = encodeURIComponent(signerId);
    const vid = versionIds.join(',');
    return axios.post(
      `https://pactsafe.io/send?sid=${accessId}&gid=${groupId}&sig=${sig}&et=agreed&vid=${vid}`,
    );
  };

  const {
    mutate: sendAgreement,
    mutateAsync: sendAgreementAsync,
    isLoading,
    isError,
  } = useMutation(sendActivity, {
    onSuccess: () => {
      // Refetch all terms of service queries after new agreement
      queryClient.invalidateQueries({
        queryKey: ['termsOfService'],
        exact: false,
      });
    },
  });

  return {
    sendAgreement,
    sendAgreementAsync,
    isLoading,
    isError,
  };
}
